import React from "react";
import { useSearchParams, Link as RouterLink, useNavigate } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import * as authService from "../api/auth";

import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Alert,
  Collapse,
  IconButton,
  Link,
} from "@mui/material";
import { LockOutlined, Visibility, VisibilityOff } from "@mui/icons-material";

function decodeTokenParam(raw) {
  const s = (raw || "").trim();
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

const Schema = Yup.object().shape({
  password: Yup.string()
    .min(6, "At least 6 characters")
    .required("Password is required"),
  passwordConfirm: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm your password"),
});

export default function ResetPassword() {
  const [params] = useSearchParams();
  const raw = params.get("token");
  const token = raw ? decodeTokenParam(raw) : null;
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);
  const [showPassword2, setShowPassword2] = React.useState(false);

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          px: 2,
          background: "linear-gradient(135deg, #87CEFA 0%, #4682B4 100%)",
        }}
      >
        <Card
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 440,
            borderRadius: 4,
            p: 1,
            backgroundColor: "rgba(255,255,255,0.25)",
            backdropFilter: "blur(12px)",
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography color="#FFFFFF" paragraph>
              This reset link is missing a token. Open the link from your email,
              or request a new reset from sign in.
            </Typography>
            <Button component={RouterLink} to="/forgot-password" variant="contained" fullWidth>
              Request reset link
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        background: "linear-gradient(135deg, #87CEFA 0%, #4682B4 100%)",
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 440,
          borderRadius: 4,
          p: 1,
          backgroundColor: "rgba(255,255,255,0.25)",
          boxShadow:
            "0 10px 30px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.25)",
          backdropFilter: "blur(12px)",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Box
              component="img"
              src="/white_on_trans.png"
              alt="OnTheFarm logo"
              loading="lazy"
              sx={{
                display: "block",
                mx: "auto",
                mb: 1,
                width: { xs: 94, sm: 150, md: 190 },
                height: { xs: 94, sm: 150, md: 190 },
                objectFit: "contain",
              }}
            />
            <Typography variant="h6" color="#FFFFFF" sx={{ fontWeight: 600 }}>
              Set a new password
            </Typography>
          </Box>

          <Formik
            initialValues={{ password: "", passwordConfirm: "" }}
            validationSchema={Schema}
            validateOnMount
            onSubmit={async (
              values,
              { setSubmitting, setStatus }
            ) => {
              setStatus(undefined);
              try {
                await authService.resetPassword({
                  token,
                  password: values.password.trim(),
                });
                navigate("/login", {
                  replace: true,
                  state: { passwordResetComplete: true },
                });
              } catch (err) {
                const detail = err?.response?.data?.detail;
                setStatus({
                  serverError:
                    (Array.isArray(detail)
                      ? detail.map((x) => x.msg || x).join(" ")
                      : detail) ||
                    err?.message ||
                    "Could not reset password. Try requesting a new link.",
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              isSubmitting,
              errors,
              touched,
              isValid,
              dirty,
              setFieldValue,
              status,
            }) => (
              <Form noValidate>
                <Collapse in={Boolean(status?.serverError)} unmountOnExit>
                  <Alert
                    severity="error"
                    variant="filled"
                    sx={{ mb: 2, borderRadius: 2 }}
                  >
                    {status?.serverError}
                  </Alert>
                </Collapse>

                <Field
                  as={TextField}
                  name="password"
                  label="New password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  margin="normal"
                  autoComplete="new-password"
                  onBlur={(e) =>
                    setFieldValue("password", e.target.value.trim())
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((s) => !s)}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.6)",
                    },
                  }}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                />

                <Field
                  as={TextField}
                  name="passwordConfirm"
                  label="Confirm new password"
                  type={showPassword2 ? "text" : "password"}
                  fullWidth
                  margin="normal"
                  autoComplete="new-password"
                  onBlur={(e) =>
                    setFieldValue("passwordConfirm", e.target.value.trim())
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          aria-label="toggle confirm password visibility"
                          onClick={() => setShowPassword2((s) => !s)}
                        >
                          {showPassword2 ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.6)",
                    },
                  }}
                  error={
                    touched.passwordConfirm && Boolean(errors.passwordConfirm)
                  }
                  helperText={
                    touched.passwordConfirm && errors.passwordConfirm
                  }
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isSubmitting || !isValid || !dirty}
                  sx={{
                    mt: 2,
                    py: 1.3,
                    borderRadius: 2.5,
                    textTransform: "uppercase",
                    letterSpacing: 1.2,
                    fontWeight: 600,
                    boxShadow: "0 8px 18px rgba(0,0,0,0.2)",
                    background:
                      "linear-gradient(135deg, #1E90FF 0%, #00BFFF 100%)",
                    "&:hover": {
                      boxShadow: "0 10px 22px rgba(0,0,0,0.25)",
                      filter: "brightness(0.95)",
                    },
                  }}
                >
                  {isSubmitting ? "Saving…" : "Update password"}
                </Button>

                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <Link component={RouterLink} to="/login" underline="hover">
                    Back to sign in
                  </Link>
                </Box>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Box>
  );
}
